
import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Video, Pause, Play, StopCircle, RefreshCcw, Clock, Loader2, CheckCircle, Timer } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface VideoRecorderProps {
  onRecordingComplete: (recordingUrl: string) => void;
  maxRecordingTime?: number;
}

const VideoRecorder = ({ onRecordingComplete, maxRecordingTime = 300 }: VideoRecorderProps) => {
  const [permission, setPermission] = useState<boolean | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordingStatus, setRecordingStatus] = useState<'idle' | 'recording' | 'paused' | 'stopped'>('idle');
  const [recordedVideo, setRecordedVideo] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isBeforeRecording, setIsBeforeRecording] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isTimeWarning, setIsTimeWarning] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      // Clean up stream when component unmounts
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Check if approaching time limit
    if (recordingStatus === 'recording' && maxRecordingTime) {
      if (recordingTime >= maxRecordingTime - 30 && recordingTime < maxRecordingTime) {
        setIsTimeWarning(true);
      }
      
      // Auto-stop recording when time limit is reached
      if (recordingTime >= maxRecordingTime) {
        stopRecording();
        toast.info("Maximum recording time reached", {
          description: "Your recording has been stopped as it reached the 5-minute limit."
        });
      }
    }
  }, [recordingTime, maxRecordingTime, recordingStatus]);

  const getCameraPermission = async () => {
    setIsLoading(true);
    
    try {
      const audioConstraints = { audio: true };
      const videoConstraints = { 
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        }
      };
      
      // Get audio permission first
      await navigator.mediaDevices.getUserMedia(audioConstraints);
      
      // Then get video stream
      const cameraStream = await navigator.mediaDevices.getUserMedia({
        ...audioConstraints,
        ...videoConstraints
      });

      setPermission(true);
      setStream(cameraStream);
      streamRef.current = cameraStream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = cameraStream;
      }
    } catch (error) {
      console.error('Error getting camera permission:', error);
      toast.error('Could not access camera or microphone. Please check permissions.');
      setPermission(false);
    } finally {
      setIsLoading(false);
    }
  };

  const startRecording = () => {
    setIsBeforeRecording(false);
    setRecordingStatus('recording');
    setRecordingTime(0);
    setIsTimeWarning(false);
    
    chunksRef.current = [];
    
    const mediaRecorder = new MediaRecorder(streamRef.current!);
    mediaRecorderRef.current = mediaRecorder;
    
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };
    
    mediaRecorder.onstop = () => {
      const videoBlob = new Blob(chunksRef.current, { type: 'video/webm' });
      const videoUrl = URL.createObjectURL(videoBlob);
      setRecordedVideo(videoUrl);
      
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = videoUrl;
        videoRef.current.muted = false;
      }
    };
    
    mediaRecorder.start(1000);
    
    // Start timer
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && recordingStatus === 'recording') {
      mediaRecorderRef.current.pause();
      setRecordingStatus('paused');
      
      // Pause timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && recordingStatus === 'paused') {
      mediaRecorderRef.current.resume();
      setRecordingStatus('recording');
      
      // Resume timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecordingStatus('stopped');
      
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      setIsPreviewMode(true);
    }
  };

  const retryRecording = () => {
    setRecordedVideo(null);
    setRecordingStatus('idle');
    setIsPreviewMode(false);
    setIsBeforeRecording(true);
    setIsTimeWarning(false);
    getCameraPermission();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const submitRecording = () => {
    if (!recordedVideo) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        const newProgress = prev + 10;
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setIsUploading(false);
            onRecordingComplete(recordedVideo);
            toast.success('Video uploaded successfully');
          }, 500);
          return 100;
        }
        return newProgress;
      });
    }, 300);
  };

  const getTimeLeftDisplay = () => {
    if (maxRecordingTime) {
      const timeLeft = maxRecordingTime - recordingTime;
      return formatTime(timeLeft);
    }
    return "";
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white">
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Video className="text-apple-blue mr-2" size={20} />
            <h3 className="text-lg font-medium text-apple-text">Video Introduction</h3>
          </div>
          {recordingStatus === 'recording' && (
            <div className="flex items-center">
              <Timer className="text-gray-500 mr-1" size={16} />
              <span className="text-sm font-medium text-gray-700">Time left: {getTimeLeftDisplay()}</span>
              {isTimeWarning && (
                <Badge variant="destructive" className="ml-2 animate-pulse">
                  Less than 30s left
                </Badge>
              )}
            </div>
          )}
        </div>
        
        <div className="relative bg-gray-900 rounded-lg overflow-hidden mb-4 aspect-video">
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
              <Loader2 className="animate-spin text-white" size={40} />
            </div>
          ) : !permission ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 text-white">
              <Video size={48} className="mb-4" />
              <p className="text-lg font-medium mb-2">Camera access required</p>
              <p className="text-sm text-gray-300 mb-6 text-center max-w-xs">
                We need access to your camera and microphone to record your introduction.
              </p>
              <button
                onClick={getCameraPermission}
                className="px-5 py-2 bg-apple-blue rounded-full font-medium transition-all hover:bg-opacity-90"
              >
                Allow Camera Access
              </button>
            </div>
          ) : isBeforeRecording ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 text-white">
              <div className="text-center max-w-md px-6">
                <h4 className="text-xl font-medium mb-2">Ready to record?</h4>
                <p className="text-sm text-gray-300 mb-2">
                  You'll have up to 5 minutes to introduce yourself and discuss your experience.
                </p>
                <div className="mb-4 inline-flex items-center px-3 py-1 bg-gray-700 rounded-full">
                  <Clock className="text-blue-400 mr-2" size={14} />
                  <span className="text-xs font-medium">Maximum 5 minutes</span>
                </div>
                <button
                  onClick={startRecording}
                  className="px-6 py-2.5 bg-red-500 rounded-full font-medium transition-all hover:bg-opacity-90 flex items-center justify-center mx-auto"
                >
                  Start Recording
                </button>
              </div>
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted={!isPreviewMode}
              className="w-full h-full object-cover"
              controls={isPreviewMode}
            ></video>
          )}
          
          {recordingStatus === 'recording' && (
            <div className="absolute top-4 left-4 flex items-center px-3 py-1.5 bg-red-500 rounded-full text-white text-sm font-medium">
              <div className="w-3 h-3 rounded-full bg-white mr-2 record-pulse"></div>
              <span>{formatTime(recordingTime)}</span>
            </div>
          )}
        </div>
        
        {permission && !isBeforeRecording && (
          <div className="flex flex-wrap justify-center gap-3 mb-4">
            {recordingStatus === 'recording' && (
              <>
                <button
                  onClick={pauseRecording}
                  className="flex items-center px-4 py-2 bg-gray-200 rounded-full text-apple-text font-medium transition-all hover:bg-gray-300"
                >
                  <Pause size={18} className="mr-2" />
                  Pause
                </button>
                <button
                  onClick={stopRecording}
                  className="flex items-center px-4 py-2 bg-red-500 text-white rounded-full font-medium transition-all hover:bg-red-600"
                >
                  <StopCircle size={18} className="mr-2" />
                  Stop Recording
                </button>
              </>
            )}
            
            {recordingStatus === 'paused' && (
              <>
                <button
                  onClick={resumeRecording}
                  className="flex items-center px-4 py-2 bg-gray-200 rounded-full text-apple-text font-medium transition-all hover:bg-gray-300"
                >
                  <Play size={18} className="mr-2" />
                  Resume
                </button>
                <button
                  onClick={stopRecording}
                  className="flex items-center px-4 py-2 bg-red-500 text-white rounded-full font-medium transition-all hover:bg-red-600"
                >
                  <StopCircle size={18} className="mr-2" />
                  Stop Recording
                </button>
              </>
            )}
            
            {recordingStatus === 'stopped' && (
              <>
                <button
                  onClick={retryRecording}
                  className="flex items-center px-4 py-2 bg-gray-200 rounded-full text-apple-text font-medium transition-all hover:bg-gray-300"
                >
                  <RefreshCcw size={18} className="mr-2" />
                  Record Again
                </button>
                <button
                  onClick={submitRecording}
                  className="flex items-center px-4 py-2 bg-apple-blue text-white rounded-full font-medium transition-all hover:bg-opacity-90"
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} className="mr-2" />
                      Submit Recording
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        )}
        
        {isUploading && (
          <div>
            <div className="flex justify-between items-center text-sm mb-1">
              <span className="text-apple-text-secondary">Uploading video...</span>
              <span className="text-apple-text-secondary">{uploadProgress}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-apple-blue transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-100">
          <h4 className="font-medium text-apple-text mb-2">Tips for a great video:</h4>
          <ul className="text-sm text-apple-text-secondary space-y-1">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              Find a quiet, well-lit space with a neutral background
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              Position your camera at eye level and look directly into it
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              Speak clearly and at a moderate pace
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              Briefly introduce yourself and highlight your relevant experience for this position
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              Include specific examples of your core skills and achievements
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default VideoRecorder;
